import { areShallowEqual } from './objects';

/**
 * Creates a memoizer for a single-value function, backed by a WeakMap.
 * With a WeakMap, the memoized values are only kept as long as the source objects,
 * ensuring that there is no memory leak.
 * @internal
 */
export function createMemoizer<K extends object, V>(create: (old: K) => V): (current: K) => V {
    const cache = new WeakMap<K, V>();

    return (current: K): V => {
        if (cache.has(current)) {
            // tslint:disable-next-line:no-non-null-assertion
            return cache.get(current)!;
        }

        const value = create(current);

        cache.set(current, value);

        return value;
    };
}

/**
 * Interface for a function which can be memoized efficiently.
 * @public
 */
export interface IMemoizable<TProps, TResult> {
    (props: TProps): TResult;
}

/**
 * Utility class which remembers the last input props and last result for a memoized function invocation.
 * This can help avoid re-computations if the inputs to a function have not changed since the last
 * execution.
 *
 * To use this class, create an instance of Memoizer within the constructor of a React component.
 * Each function used for memoization should be declared in a higher scope, or else the memoization
 * will not be able to track it properly.
 *
 * @public
 */
export class Memoizer {
    /**
     * Maps each function to the props and result of its last invocation
     * with respect to this memoizer.
     */
    private _lastInvocations: WeakMap<Function, {
        props: {};
        result: {};
    }>;

    constructor() {
        this._lastInvocations = new WeakMap();
    }

    /**
     * Gets the memoized version of the input function for invocation.
     * @param memoizable - A memoizable function to be memoized for subsequent invocation.
     */
    public evaluate<TProps, TResult>(memoizable: IMemoizable<TProps, TResult>): IMemoizable<TProps, TResult> {
        const lastInvocations = this._lastInvocations;

        return (props: TProps): TResult => {
            const lastInvocation = lastInvocations.get(memoizable);

            if (!lastInvocation || !areShallowEqual(lastInvocation.props, props)) {
                const result = memoizable(props);

                lastInvocations.set(memoizable, {
                    props: props,
                    result: result
                });

                return result;
            }

            return lastInvocation.result as TResult;
        };
    }
}
